import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DisplayGrid, Gridster, GridsterConfig, GridsterItem } from 'angular-gridster2';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SelectModule } from 'primeng/select';
import { BehaviorSubject, debounceTime, startWith } from 'rxjs';

import {
  WidgetConfiguration,
  WidgetConfigurationType,
  WidgetType,
} from '../../models/widget-configuration.model';
import { WidgetRegistry } from '../../widget-registry.service';
import { WidgetService } from '../../widget.service';
import { WidgetCardComponent } from '../widget-card/widget-card.component';
import { WidgetCreationComponent } from '../widget-creation/widget-creation.component';

@Component({
  selector: 'carehub-widget-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    Gridster,
    GridsterItem,
    WidgetCardComponent,
    WidgetCreationComponent,
    ButtonModule,
    ButtonGroupModule,
    CardModule,
    ReactiveFormsModule,
    FormsModule,
    AsyncPipe,
    SelectModule,
    ConfirmDialogModule,
  ],
  templateUrl: './widget-dashboard.component.html',
  styleUrl: './widget-dashboard.component.scss',
})
export class WidgetDashboardComponent implements OnInit, OnDestroy {
  @ViewChild(Gridster) gridster?: Gridster;
  @Input() input: WidgetConfiguration | undefined;

  isWidgetCreationDialogVisible: boolean = false;
  canAddWidget: boolean = true;
  selectedWidgetType: WidgetType | null = null;

  options: GridsterConfig;
  widgets$: BehaviorSubject<WidgetConfiguration[]> = new BehaviorSubject<WidgetConfiguration[]>([]);

  isEditing: boolean = false;
  widgetTypes: { label: string; value: WidgetType }[] = [];

  private settingsLoaded: boolean = false;
  private lastSavedWidgetsSnapshot: string = '';

  constructor(
    private widgetService: WidgetService,
    private widgetRegistry: WidgetRegistry,
    private confirmationService: ConfirmationService
  ) {
    this.options = {
      itemChangeCallback: (): void => this.updateCanAddWidget(),
      itemResizeCallback: (): void => this.updateCanAddWidget(),
      minCols: 30,
      maxCols: 30,
      minRows: 30,
      maxRows: 30,
      minItemCols: 1,
      minItemRows: 1,
      draggable: { enabled: false },
      resizable: { enabled: false },
      displayGrid: DisplayGrid.OnDragAndResize,
      compactType: 'none',
      gridType: 'fit',
      mobileBreakpoint: 0,
      pushItems: true,
    };

    this.widgetTypes = this.widgetRegistry.getAvailableWidgets();
  }

  ngOnInit(): void {
    this.widgetService.getDashboardSettings().subscribe((configurations: WidgetConfiguration[]) => {
      this.lastSavedWidgetsSnapshot = this.getWidgetsSignature(configurations);
      this.settingsLoaded = true;
      this.widgets$.next(configurations);
    });

    this.widgets$.pipe(debounceTime(100)).subscribe((configurations) => {
      const element: HTMLElement | null = document.querySelector(
        'gridster-preview'
      ) as HTMLElement | null;
      if (element) {
        element.style.display = '';
      }

      if (!this.settingsLoaded) {
        return;
      }

      const currentSignature: string = this.getWidgetsSignature(configurations);
      if (currentSignature === this.lastSavedWidgetsSnapshot) {
        return;
      }

      this.widgetService.createDashboardSettings(configurations).subscribe({
        next: () => {
          this.lastSavedWidgetsSnapshot = currentSignature;
        },
      });
    });

    this.widgets$.pipe(startWith([])).subscribe(() => {
      this.updateCanAddWidget();
    });
  }

  private updateCanAddWidget(widgetType?: WidgetType): void {
    if (!widgetType && this.selectedWidgetType) {
      widgetType = this.selectedWidgetType;
    }

    if (!widgetType) {
      this.canAddWidget = true;
      return;
    }

    const size: { cols: number; rows: number } = this.widgetRegistry.getDefaultSize(widgetType);
    const canAddWidget: boolean | undefined = this.gridster?.getNextPossiblePosition({
      ...size,
      x: -1,
      y: -1,
    });

    this.canAddWidget = canAddWidget ?? false;
  }

  checkWidgetSpace(widgetType: WidgetType): boolean {
    if (!widgetType || !this.gridster) {
      return false;
    }

    const size: {
      cols: number;
      rows: number;
    } = this.widgetRegistry.getDefaultSize(widgetType);
    const canFit: boolean = this.gridster.getNextPossiblePosition({
      ...size,
      x: -1,
      y: -1,
    });

    return canFit;
  }

  onWidgetTypeSelected(widgetType: WidgetType): void {
    if (widgetType) {
      this.selectedWidgetType = widgetType;
      this.canAddWidget = this.checkWidgetSpace(widgetType);
    }
  }

  get displaySwitchButtonLabel(): string {
    return this.isEditing
      ? $localize`:@@common.button.save:Save`
      : $localize`:@@common.button.edit:Edit`;
  }

  toggleEditable(): void {
    if (this.isEditing) {
      const currentWidgets: WidgetConfiguration[] = this.widgets$.getValue();
      this.widgetService.createDashboardSettings(currentWidgets).subscribe({
        next: () => {
          this.lastSavedWidgetsSnapshot = this.getWidgetsSignature(currentWidgets);
        },
      });
    }

    this.isEditing = !this.isEditing;

    this.options = {
      ...this.options,
      draggable: { ...this.options.draggable, enabled: !this.options.draggable?.enabled },
      resizable: { enabled: !this.options.resizable?.enabled },
      displayGrid: this.isEditing ? 'always' : 'none',
    };
  }

  deleteWidget(widget: WidgetConfiguration): void {
    this.confirmationService.confirm({
      message: $localize`Are you sure that you want to delete this widget?`,
      accept: () => {
        const currentWidgets: WidgetConfiguration[] = this.widgets$.getValue();

        const newWidgets: WidgetConfiguration[] = currentWidgets.filter(
          innerWidget => innerWidget.id !== widget.id
        );
        this.widgets$.next(newWidgets);
        setTimeout(() => {
          this.updateCanAddWidget();
        });
      },
    });
  }

  updateWidget(widget: WidgetConfiguration): void {
    const currentConfigurations: WidgetConfiguration[] = this.widgets$.getValue();
    const newWidgetConfigurations: WidgetConfiguration[] = currentConfigurations.map(
      (configuration) => {
        if (configuration.id === widget.id) {
          return widget;
        }
        return configuration;
      }
    );
    this.widgets$.next(newWidgetConfigurations);
  }

  onWidgetCreated(event: { type: WidgetType; configuration: WidgetConfigurationType }): void {
    const currentWidgets: WidgetConfiguration[] = this.widgets$.getValue();
    const widgetId: number = Math.floor(Math.random() * 1000000000);
    const defaultSize: { cols: number; rows: number } = this.widgetRegistry.getDefaultSize(event.type);

    const newWidget: WidgetConfiguration = {
      id: widgetId,
      type: event.type,
      positionConfiguration: {
        ...defaultSize,
        id: widgetId,
        x: -1, // Gridster will auto-position
        y: -1, // Gridster will auto-position
      },
      configuration: event.configuration,
    };

    this.widgets$.next([...currentWidgets, newWidget]);
    this.isWidgetCreationDialogVisible = false;
  }

  showWidgetCreationDialog(): void {
    if (this.isEditing) {
      this.toggleEditable();
    }

    this.selectedWidgetType = null;
    this.canAddWidget = true;
    this.isWidgetCreationDialogVisible = true;
  }

  ngOnDestroy(): void {
    this.widgets$.complete();
  }

  private getWidgetsSignature(widgets: WidgetConfiguration[]): string {
    return JSON.stringify(widgets);
  }
}
